import { useToast } from '@/components/feedback/toast';
import { ApiError } from '@/lib/api/errors';

import { INSTALLATION_APPROVED_MESSAGE } from '../constants';
import { useApproveInstallation } from '../hooks';
import type { ApproveInstallationBody } from '../types';

import { AssignInstallerModal } from './assign-installer-modal';

export type ApproveInstallationModalProps = {
  open: boolean;
  id: string;
  onClose: () => void;
  onApproved: () => void;
  /** Fired when the backend **refuses** the approval — a 409 (`NOT_ACTIONABLE` when another admin
   *  already actioned it, `HOLD_EXPIRED` when the seven-day hold lapsed) or any other error. The
   *  dialog stays open; the host uses this to refresh caches the mutation's own
   *  `['installations']` invalidation cannot reach. Unset on the Requests queue, whose table key
   *  happens to be `'installations'` too. */
  onRefused?: () => void;
};

/** Approve an Open installation request: the shared `<AssignInstallerModal>` collects the
 *  assignment, this wrapper owns the installation mutation and its toasts. A gateway capture
 *  failure comes back as a slim `{captureFailure: true, rejectionReason}` result rather than an
 *  error (CONTEXT.md: fee hold) — the drawer/list pick up the new status via the invalidated
 *  detail query, not from this result. */
export function ApproveInstallationModal({
  open,
  id,
  onClose,
  onApproved,
  onRefused,
}: ApproveInstallationModalProps) {
  const approve = useApproveInstallation(id);
  const toast = useToast();

  const onResult = (result: { captureFailure?: boolean; rejectionReason?: string }) => {
    if (result.captureFailure) {
      toast.error({ title: result.rejectionReason ?? 'Approval failed.' });
    } else {
      toast.success({ title: INSTALLATION_APPROVED_MESSAGE });
    }
    onApproved();
    onClose();
  };

  const submit = (body: ApproveInstallationBody) =>
    approve.mutate(body, {
      onSuccess: onResult,
      onError: (e) => {
        toast.error({
          title: e instanceof ApiError ? e.message : 'Could not approve. Please try again.',
        });
        onRefused?.();
      },
    });

  return (
    <AssignInstallerModal
      open={open}
      onClose={onClose}
      onSubmit={submit}
      loading={approve.isPending}
    />
  );
}
